/**
 * Whether the Sync action applies to a deal, and what to say when it doesn't.
 *
 * The deals list is deliberately wider than the sync gate: it shows every
 * closed deal and every deal carrying a draft/sent/signed quote, so a deal can
 * be on screen while it is still being negotiated. Syncing stays restricted to
 * the stages the tenant configured, and merged deals are refused whatever
 * their stage — so every Sync affordance has to ask this first rather than
 * offering an action that will come back refused.
 */

/**
 * Why a deal cannot be synced, as sent by the API in `sync_ineligible_code`.
 * Kept in step with backend/services/deal_visibility.py.
 */
export const SYNC_BLOCKED_STAGE = 'deal_stage_not_allowed';
export const SYNC_BLOCKED_MERGED = 'deal_merged';

interface SyncGateFields {
  sync_eligible?: boolean;
  sync_ineligible_reason?: string | null;
  sync_ineligible_code?: string | null;
  dealstage?: string | null;
}

/**
 * Whether the deal cannot currently be synced.
 *
 * Only an explicit `false` blocks: the field is absent on responses from an
 * older API and on objects built client-side, and treating "unknown" as
 * blocked would disable the action everywhere it isn't sent.
 */
export function isSyncBlocked(deal: SyncGateFields): boolean {
  return deal.sync_eligible === false;
}

/**
 * The reason code, treating a response that predates the field as a stage
 * block — back then that was the only reason a deal could be refused.
 */
function blockedCode(deal: SyncGateFields): string {
  return deal.sync_ineligible_code || SYNC_BLOCKED_STAGE;
}

/** What the disabled control says, in the space a menu item has. */
export function syncBlockedLabel(deal: SyncGateFields): string {
  switch (blockedCode(deal)) {
    case SYNC_BLOCKED_MERGED:
      return 'Merged deal';
    case SYNC_BLOCKED_STAGE:
      return 'Stage not ready to sync';
    default:
      // A reason this build has no copy for. Say only what is certainly true;
      // the sentence below carries the server's own explanation.
      return 'Not ready to sync';
  }
}

/**
 * The explanation to show, as a sentence.
 *
 * Mostly written here rather than passed through from the API: the server's
 * stage reason lists raw stage ids ("not in allowed stages: ['closedwon']")
 * because it also answers API clients, and that is not what someone looking
 * at a deal needs to read. It says where the setting lives, since changing it
 * is the only way to make the action available.
 *
 * Which copy applies is decided by the reason code, never assumed. This used
 * to return the stage sentence unconditionally, so a merged deal — refused
 * for a reason that has nothing to do with its stage — was told "syncing is
 * limited to the deal stages configured in Settings; this deal is in
 * 'closedwon'", naming the very stage the tenant had configured. An unknown
 * code falls back to the server's own sentence, which is at worst blunt but
 * never wrong.
 */
export function syncBlockedDetail(deal: SyncGateFields): string {
  switch (blockedCode(deal)) {
    case SYNC_BLOCKED_MERGED:
      return (
        'This deal was merged in HubSpot, and merged deals are not supported ' +
        'for syncing. You can still link it to an existing Stripe ' +
        'subscription or invoice.'
      );
    case SYNC_BLOCKED_STAGE: {
      const stage = deal.dealstage?.trim();
      const where = 'Syncing is limited to the deal stages configured in Settings';
      return stage ? `${where}; this deal is in "${stage}".` : `${where}.`;
    }
    default:
      return deal.sync_ineligible_reason?.trim() || 'This deal cannot be synced.';
  }
}

/** The API's own wording, for a title attribute — the exact reason a sync
 *  would be refused, for anyone diagnosing why. */
export function syncBlockedApiReason(deal: SyncGateFields): string | undefined {
  return deal.sync_ineligible_reason || undefined;
}
